import { TDocument } from "@shared/entities";
import { DocumentSchema } from "@shared/entities/document";

/** Documents the clinic hands to the patient when it approves a request: the
 *  issued license, the prescriptions, the doctor's recommendation. The same
 *  three types live on the profile, so an approval can copy them there. */
export const CLIENT_DOCUMENT_TYPES = ["CANNABIS_LICENSE", "PRESCRIPTIONS", "DOCTOR_RECOMMENDATION"] as const;

export type TClientDocumentType = (typeof CLIENT_DOCUMENT_TYPES)[number];

export type TClientDocuments = Partial<Record<TClientDocumentType, TDocument | null>>;

/** The field writes an approval makes, by dotted path, so a type the dialog
 *  did not touch is never written at all:
 *  - a document present in the dialog → set on the request, and copied to the
 *    profile;
 *  - a document the admin removed, that was saved before → removed from the
 *    request only (the profile keeps whatever it has);
 *  - anything else → no write.
 *
 *  A document is copied to the profile only if it passes DocumentSchema — the
 *  same check the server's updateClientProfile runs on the whole documents map.
 *  One malformed entry there (say an upper-case ".PDF") would make every later
 *  profile edit by the client fail, so it stays on the request instead and is
 *  listed in `notCopied`.
 *  `remove` is Firestore's deleteField, passed in to keep this pure. */
export function approvalFieldUpdates(
	documentsForClient: TClientDocuments,
	savedDocumentsForClient: TClientDocuments,
	remove: () => unknown,
): { request: Record<string, unknown>; profile: Record<string, unknown>; notCopied: TClientDocumentType[] } {
	const request: Record<string, unknown> = {};
	const profile: Record<string, unknown> = {};
	const notCopied: TClientDocumentType[] = [];
	for (const type of CLIENT_DOCUMENT_TYPES) {
		const document = documentsForClient[type];
		if (document) {
			request[`documentsForClient.${type}`] = document;
			if (DocumentSchema.safeParse(document).success) profile[`documents.${type}`] = document;
			else notCopied.push(type);
		} else if (savedDocumentsForClient[type]) {
			request[`documentsForClient.${type}`] = remove();
		}
	}
	return { request, profile, notCopied };
}

/** Where the profile's own copy of an approved document is stored. The profile
 *  must not point at the request's file: deleting a request deletes everything
 *  under `requests/{id}` (onRequestDelete), and the license the pharmacy order
 *  reads would go with it. A unique name, so an earlier file — which an older
 *  pharmacy order may still point at — is never overwritten. */
export function profileCopyPath(profileId: string, document: Pick<TDocument, "type" | "extension">, now = Date.now()): string {
	return `profiles/${profileId}/documents/approved-${now}-${document.type}.${document.extension}`;
}

/** Whether deleting this document's file from Storage is safe from a profile
 *  screen. A profile entry can point at a file it does not own — anything
 *  under `requests/…` belongs to that request, which keeps pointing at it.
 *  Only files in the profile's own folder (its uploads, and the copies made
 *  on approval) may be deleted; for anything else, removing it from the
 *  profile drops the reference and leaves the file where its owner finds it. */
export function isStoredUnderProfile(document: Pick<TDocument, "path">, profileId: string): boolean {
	return !!document.path && !!profileId && document.path.startsWith(`profiles/${profileId}/`);
}
